/**
 * CustomerMismatchDialog — shown when POST /phase0/generate answers 409 INPUT_CUSTOMER_MISMATCH
 * (Release 2, T-40; demo L755–776: "a wrong customer information ... put some guardrails around that").
 *
 * The server classified one or more uploaded inputs as being about a DIFFERENT customer than this
 * project. Two ways forward:
 *   • Remove file   — the existing DELETE /phase0/files/{filename} action, per file. Once every
 *                     flagged file is gone the dialog offers to generate again.
 *   • Generate anyway — a second, explicit confirmation step explaining the override is AUDITED
 *                     (who, when, which files), then resends with `override_customer_check: true`.
 *
 * Presentational: no API calls of its own; the parent owns the mutations.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { InputCustomerMismatchDetails } from "@/types";

export interface CustomerMismatchDialogProps {
  open: boolean;
  details: InputCustomerMismatchDetails | null;
  /** May the caller remove inputs / generate (pm, sa, leadership, admin). */
  canManage: boolean;
  /** Filename currently being removed, if any. */
  removingFile: string | null;
  /** Files already removed from this list in this session. */
  removedFiles: readonly string[];
  onRemoveFile: (filename: string) => void;
  onGenerateAnyway: () => void;
  onGenerateAgain: () => void;
  onClose: () => void;
}

export function CustomerMismatchDialog({
  open,
  details,
  canManage,
  removingFile,
  removedFiles,
  onRemoveFile,
  onGenerateAnyway,
  onGenerateAgain,
  onClose,
}: CustomerMismatchDialogProps): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  // Every re-open starts at the list, never pre-armed on the confirmation step.
  useEffect(() => {
    if (open) setConfirming(false);
  }, [open]);

  const files = details?.files ?? [];
  const remaining = files.filter((f) => !removedFiles.includes(f.file));
  const allRemoved = files.length > 0 && remaining.length === 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* max-h + overflow: a long file list scrolls inside the dialog instead of past it. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {!confirming ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-start gap-2 pr-6">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-error" aria-hidden="true" />
                These files look like a different customer
              </DialogTitle>
              <DialogDescription>
                This project is for <strong>{details?.expected || "this customer"}</strong>, but the files below
                appear to be about someone else. Generating from them could put another customer&apos;s
                information into this project&apos;s artifacts. Generation was not started.
              </DialogDescription>
            </DialogHeader>
            {/* min-w-0: as a grid item the list would otherwise grow to its longest unbroken
                file name and push past the dialog edge; with it, names truncate. */}
            <ul className="min-w-0 space-y-2 py-1" aria-label="Files that look like another customer">
              {files.map((f) => {
                const removed = removedFiles.includes(f.file);
                return (
                  <li
                    key={f.file}
                    className="flex items-center justify-between gap-3 rounded-md border border-outlines p-3 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-text-primary" title={f.file}>
                        {f.file}
                      </p>
                      <p className="break-words text-text-secondary">
                        {removed ? "Removed" : `Looks like: ${f.detected_customer ?? "another customer"}`}
                      </p>
                    </div>
                    {canManage && !removed && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="shrink-0"
                        onClick={() => onRemoveFile(f.file)}
                        disabled={removingFile !== null}
                        aria-label={`Remove file ${f.file}`}
                      >
                        {removingFile === f.file ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        ) : (
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        )}
                        Remove file
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {canManage &&
                (allRemoved ? (
                  <Button onClick={onGenerateAgain}>Generate again</Button>
                ) : (
                  <Button variant="danger" onClick={() => setConfirming(true)} disabled={removingFile !== null}>
                    Generate anyway…
                  </Button>
                ))}
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Generate anyway?</DialogTitle>
              <DialogDescription>
                Artifacts will be generated from {remaining.length === 1 ? "a file" : `${remaining.length} files`}{" "}
                that appear to be about another customer. This override is recorded in the project&apos;s audit
                log with your name, the time and the files involved.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirming(false)}>
                Back
              </Button>
              <Button variant="danger" onClick={onGenerateAnyway}>
                Yes, generate anyway
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CustomerMismatchDialog;
